import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { decodeWaveformPayload, resamplePeaks } from '../utils/waveform';

/** Generate a deterministic pseudo-random waveform from a seed string.
 *  Produces natural-looking peaks without downloading/decoding the audio. */
const generateWaveform = (seed: string, barCount: number): number[] => {
  // Simple hash from string
  let hash = 0;
  for (let index = 0; index < seed.length; index++) {
    hash = (hash * 31 + seed.charCodeAt(index)) | 0;
  }

  const peaks: number[] = [];
  for (let index = 0; index < barCount; index++) {
    // Deterministic PRNG (mulberry32)
    hash = (hash + 0x6d2b79f5) | 0;
    let randomState = Math.imul(hash ^ (hash >>> 15), 1 | hash);
    randomState = (randomState + Math.imul(randomState ^ (randomState >>> 7), 61 | randomState)) ^ randomState;
    const random = ((randomState ^ (randomState >>> 14)) >>> 0) / 4294967296;

    // Natural waveform shape: base level + variation, with occasional louder peaks
    const base = 0.3;
    const variation = 0.5;
    const peak = base + random * variation + (random > 0.85 ? 0.2 : 0);
    peaks.push(Math.min(1, peak));
  }

  return peaks;
};

const drawWaveform = (
  canvas: HTMLCanvasElement,
  peaks: number[],
  progress: number,
  playedColor: string,
  unplayedColor: string,
  barGap: number,
  barRadius: number,
) => {
  const context = canvas.getContext('2d');
  if (!context) return;

  const pixelRatio = window.devicePixelRatio || 1;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;

  canvas.width = width * pixelRatio;
  canvas.height = height * pixelRatio;
  context.scale(pixelRatio, pixelRatio);
  context.clearRect(0, 0, width, height);

  const barCount = peaks.length;
  const totalBarWidth = width / barCount;
  const actualBarWidth = Math.max(1, totalBarWidth - barGap);
  const progressX = progress * width;

  for (let index = 0; index < barCount; index++) {
    const x = index * totalBarWidth;
    const peakHeight = Math.max(2, peaks[index] * height);
    const y = (height - peakHeight) / 2;

    context.fillStyle = x < progressX ? playedColor : unplayedColor;

    if (barRadius > 0) {
      context.beginPath();
      context.roundRect(x, y, actualBarWidth, peakHeight, barRadius);
      context.fill();
    } else {
      context.fillRect(x, y, actualBarWidth, peakHeight);
    }
  }
};

interface WaveformCanvasOptions {
  audioUrl: string;
  title?: string;
  waveform?: unknown;
  progress: number;
  currentTime: number;
  duration: number;
  seekTo: (ratio: number) => void;
  primaryColor: string;
  secondaryColor: string;
  barWidth?: number;
  barGap?: number;
  resampleOnResize?: boolean;
}

const useWaveformCanvas = ({
  audioUrl,
  title,
  waveform,
  progress,
  currentTime,
  duration,
  seekTo,
  primaryColor,
  secondaryColor,
  barWidth = 2,
  barGap = 1,
  resampleOnResize = false,
}: WaveformCanvasOptions) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [peaks, setPeaks] = useState<number[]>([]);
  const [isDecoding, setIsDecoding] = useState(true);

  const barRadius = 2;

  // Refs for ResizeObserver to avoid teardown/recreation on every progress tick
  const progressRef = useRef(progress);
  const primaryColorRef = useRef(primaryColor);
  const secondaryColorRef = useRef(secondaryColor);
  const peaksRef = useRef(peaks);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);
  useEffect(() => {
    primaryColorRef.current = primaryColor;
  }, [primaryColor]);
  useEffect(() => {
    secondaryColorRef.current = secondaryColor;
  }, [secondaryColor]);
  useEffect(() => {
    peaksRef.current = peaks;
  }, [peaks]);

  // Decode real waveform payload if available (memoized since it only depends on the payload)
  const decodedPeaks = useMemo(() => decodeWaveformPayload(waveform), [waveform]);

  // Generate or resample peaks to match canvas bar count
  const waveformSeed = audioUrl || title || 'audio';
  useEffect(() => {
    const canvasWidth = canvasRef.current?.clientWidth || 1200;
    const totalBarWidth = barWidth + barGap;
    const barCount = Math.floor(canvasWidth / totalBarWidth);

    if (decodedPeaks) {
      // Use real waveform data, resampled to match canvas bar count
      setPeaks(resamplePeaks(decodedPeaks, barCount));
    } else {
      // Fall back to deterministic pseudo-random waveform
      setPeaks(generateWaveform(waveformSeed, barCount));
    }
    setIsDecoding(false);
  }, [decodedPeaks, waveformSeed, barWidth, barGap]);

  // Redraw waveform on progress or resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || peaks.length === 0) return;

    drawWaveform(canvas, peaks, progress / 100, primaryColor, secondaryColor, barGap, barRadius);
  }, [peaks, progress, primaryColor, secondaryColor, barGap]);

  // Handle resize to recalculate bar count
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver(() => {
      const barCount = Math.max(1, Math.floor(canvas.clientWidth / (barWidth + barGap)));
      if (resampleOnResize && peaksRef.current.length !== barCount) {
        setPeaks(decodedPeaks ? resamplePeaks(decodedPeaks, barCount) : generateWaveform(waveformSeed, barCount));
      }
      if (peaksRef.current.length > 0) {
        drawWaveform(
          canvas,
          peaksRef.current,
          progressRef.current / 100,
          primaryColorRef.current,
          secondaryColorRef.current,
          barGap,
          barRadius,
        );
      }
    });

    observer.observe(canvas);
    return () => observer.disconnect();
  }, [barWidth, barGap, decodedPeaks, waveformSeed, resampleOnResize]);

  const handleCanvasClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas || !duration) return;
      const rect = canvas.getBoundingClientRect();
      const ratio = (event.clientX - rect.left) / rect.width;
      seekTo(ratio);
    },
    [seekTo, duration],
  );

  const handleCanvasKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLCanvasElement>) => {
      if (!duration) return;
      const step = 0.02;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        seekTo(Math.min(1, currentTime / duration + step));
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        seekTo(Math.max(0, currentTime / duration - step));
      }
    },
    [duration, currentTime, seekTo],
  );

  return { canvasRef, isDecoding, handleCanvasClick, handleCanvasKeyDown };
};

export default useWaveformCanvas;
